import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import Placeholder from './Placeholder.jsx';
import SectionHeading from '../components/HowItWorksPanel.jsx';

// Session 10: GMB Audit, client page (/gmb-audit).
//
// Only a released audit shows (v_portal_gmb_audit already hides everything
// else from a client; the agency, who sees every status, gets the same
// "released only" rule here so the page matches what the client sees).
// Otherwise the entitled "We're setting this up for you" placeholder.
//
// GmbAuditView is shared with the agency page's "Preview as client" panel
// and its released view: it renders header, PDF, downloads and checklist from
// an audit row + task rows and never talks to the database itself except for
// signed download URLs.

export const BUCKET = 'gmb-audits';
const SIGNED_URL_TTL = 600;             // 10 minutes; re-signed on every load
export const OWNER_LABEL = { client: 'You', agency: 'Digital Lotus', developer: 'Your developer' };
const OWNER_ORDER = ['client', 'agency', 'developer'];
const FILTERS = [['client', 'Yours'], ['all', 'All'], ['agency', 'Digital Lotus'], ['developer', 'Your developer']];
const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, supporting: 2, low: 3, question: 3 };
const rank = (p) => PRIORITY_RANK[String(p || '').toLowerCase()] ?? 4;

// Undone first, then priority (Critical, High, Medium, Low), then file order.
export function sortTasks(tasks) {
  return [...tasks].sort((a, b) =>
    ((a.done_at ? 1 : 0) - (b.done_at ? 1 : 0)) || (rank(a.priority) - rank(b.priority)) || (a.sort - b.sort));
}

export async function signedUrl(path, ttl = SIGNED_URL_TTL) {
  const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, ttl);
  if (error) throw error;
  return data.signedUrl;
}

export async function downloadFile(path) {
  const url = await signedUrl(path, 300);
  window.location.href = url;
}

function useMediaQuery(query) {
  const [match, setMatch] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false));
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = (e) => setMatch(e.matches);
    setMatch(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, [query]);
  return match;
}

const paragraphs = (s) => String(s || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

// ---------------------------------------------------------------------------

export default function GmbAudit() {
  const { activeId, isAgency } = useAuth();
  const [state, setState] = useState({ loading: true, audit: null, tasks: [], error: null });
  const [banner, setBanner] = useState(null);

  const load = useCallback(async () => {
    if (!activeId) return;
    const { data: audit, error } = await supabase.from('v_portal_gmb_audit').select('*').eq('client_id', activeId).maybeSingle();
    if (error) { setState({ loading: false, audit: null, tasks: [], error: error.message }); return; }
    if (!audit || audit.status !== 'released') { setState({ loading: false, audit: null, tasks: [], error: null }); return; }
    const { data: tasks, error: tErr } = await supabase.from('v_portal_gmb_tasks').select('*').eq('audit_id', audit.audit_id).order('sort');
    if (tErr) { setState({ loading: false, audit, tasks: [], error: tErr.message }); return; }
    // The agency's view includes hidden rows; the client never sees them.
    setState({ loading: false, audit, tasks: (tasks || []).filter((t) => !t.hidden), error: null });
  }, [activeId]);

  useEffect(() => { setState((s) => ({ ...s, loading: true })); load(); }, [load]);

  // First open: clears the client's Inbox item. The RPC is a no-op for the agency.
  useEffect(() => {
    const a = state.audit;
    if (!a || isAgency || a.client_first_viewed_at) return;
    supabase.rpc('portal_gmb_mark_viewed', { p_audit_id: a.audit_id }).then(({ error }) => {
      if (!error) setState((s) => (s.audit ? { ...s, audit: { ...s.audit, client_first_viewed_at: new Date().toISOString() } } : s));
    });
  }, [state.audit, isAgency]);

  async function onToggle(task, done) {
    // Optimistic; roll back on error.
    const stamp = done ? new Date().toISOString() : null;
    setState((s) => ({ ...s, tasks: s.tasks.map((t) => (t.task_id === task.task_id ? { ...t, done_at: stamp } : t)) }));
    const { error } = await supabase.rpc('portal_gmb_toggle_task', { p_task_id: task.task_id, p_done: done });
    if (error) {
      setState((s) => ({ ...s, tasks: s.tasks.map((t) => (t.task_id === task.task_id ? { ...t, done_at: task.done_at } : t)) }));
      setBanner({ tone: 'err', text: `Couldn't save that tick: ${error.message}` });
    } else {
      setBanner(null);
    }
  }

  if (state.loading) return <p className="lede">Loading your review…</p>;
  if (state.error) return <div className="empty"><h2>Couldn't load your review</h2><p>{state.error}</p></div>;
  if (!state.audit) return <Placeholder />;

  return (
    <>
      {banner && <div className={`banner banner-${banner.tone}`}>{banner.text}</div>}
      <GmbAuditView audit={state.audit} tasks={state.tasks} canTick={(t) => !!t.can_tick} onToggle={onToggle} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Shared view. `canTick(task)` decides whether a checkbox is enabled;
// `onToggle` null = preview (checkboxes switched off).
// ---------------------------------------------------------------------------

export function GmbAuditView({ audit, tasks, canTick, onToggle, preview = false }) {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfErr, setPdfErr] = useState(null);
  const hasClientTasks = tasks.some((t) => t.owner_group === 'client');
  const [filter, setFilter] = useState(hasClientTasks ? 'client' : 'all');
  const [open, setOpen] = useState(() => new Set());
  const [dlErr, setDlErr] = useState(null);

  useEffect(() => { setFilter(hasClientTasks ? 'client' : 'all'); }, [audit.audit_id, hasClientTasks]);

  useEffect(() => {
    let cancelled = false;
    setPdfUrl(null); setPdfErr(null);
    if (!audit.pdf_path) return undefined;
    signedUrl(audit.pdf_path).then((u) => { if (!cancelled) setPdfUrl(u); })
      .catch((e) => { if (!cancelled) setPdfErr(e.message || String(e)); });
    return () => { cancelled = true; };
  }, [audit.pdf_path, audit.audit_id]);

  const counts = useMemo(() => {
    const c = { total: tasks.length, done: tasks.filter((t) => t.done_at).length };
    for (const g of OWNER_ORDER) {
      const list = tasks.filter((t) => t.owner_group === g);
      c[g] = { total: list.length, done: list.filter((t) => t.done_at).length };
    }
    return c;
  }, [tasks]);

  const shown = useMemo(() => sortTasks(filter === 'all' ? tasks : tasks.filter((t) => t.owner_group === filter)), [tasks, filter]);
  const pct = counts.total ? Math.round((counts.done / counts.total) * 100) : 0;

  async function download(path) {
    setDlErr(null);
    try { await downloadFile(path); } catch (e) { setDlErr(e.message || String(e)); }
  }
  const toggleOpen = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="gmb">
      <header className="gmb-head">
        <SectionHeading title={audit.title}
          explainer="gmb_audit" label="your Google Business Profile review" />
        {audit.audit_date && <p className="lede">Reviewed {audit.audit_date}.</p>}
        {/* Session 13: plain intro above the score and the items */}
        <p className="gmb-intro">This is an audit of your Google Business Profile. Here is your score. Below are the changes that will improve it, and who does each one.</p>
        <div className="gmb-head-grid">
          {audit.score_total !== null && audit.score_total !== undefined && (
            <div className="tile gmb-score">
              <div className="tile-label">Your profile score</div>
              <div className="tile-value">{audit.score_total} <span className="gmb-score-of">/ 100</span>{audit.score_band ? <span className="gmb-band"> · {audit.score_band}</span> : null}</div>
              {audit.score_interpretation && <div className="tile-note gmb-score-note">{audit.score_interpretation}</div>}
            </div>
          )}
          {(audit.headline_title || audit.headline_finding) && (
            <div className="gmb-headline">
              {audit.headline_title && <h2>{audit.headline_title}</h2>}
              {paragraphs(audit.headline_finding).map((p, i) => <p key={i}>{p}</p>)}
            </div>
          )}
        </div>
        {Array.isArray(audit.stats) && audit.stats.length > 0 && (
          <div className="gmb-stats">
            {audit.stats.map((s, i) => (
              <div key={i} className={`gmb-stat ${s.highlight ? 'highlight' : ''}`}>
                <div className="gmb-stat-value">{s.value}</div>
                <div className="gmb-stat-label">{String(s.label || '').split('\n').map((l, j) => <span key={j}>{l}<br /></span>)}</div>
              </div>
            ))}
          </div>
        )}
      </header>

      <section className="gmb-section">
        <h2>Your full review</h2>
        {audit.pdf_path ? (
          pdfErr ? <p className="results-empty">The review couldn't be opened just now ({pdfErr}). Download the PowerPoint below.</p>
          : !pdfUrl ? <p className="lede">Opening the review…</p>
          : isMobile ? <a className="btn btn-primary" href={pdfUrl} target="_blank" rel="noopener noreferrer">Open full screen</a>
          : <iframe className="gmb-pdf" src={pdfUrl} title="Your full review" />
        ) : (
          <p className="results-empty">The slide-by-slide review is in the PowerPoint below.</p>
        )}
        <div className="gmb-downloads">
          {audit.deck_path && <button type="button" className="btn" onClick={() => download(audit.deck_path)}>Download PowerPoint</button>}
          {audit.approval_path && <button type="button" className="btn" onClick={() => download(audit.approval_path)}>Download the wording for your approval (.docx)</button>}
          {audit.pdf_path && !isMobile && pdfUrl && <a className="link-btn" href={pdfUrl} target="_blank" rel="noopener noreferrer">Open the PDF in a new tab</a>}
        </div>
        {dlErr && <div className="banner banner-err">Couldn't start the download: {dlErr}</div>}
      </section>

      <section className="gmb-section">
        <h2>Your checklist</h2>
        <div className="progress-card gmb-progress">
          <div className="progress-head">
            <div>
              <div className="progress-title">{counts.done} of {counts.total} done</div>
              <div className="progress-sub">
                {OWNER_ORDER.filter((g) => counts[g].total > 0)
                  .map((g) => `${OWNER_LABEL[g]}: ${counts[g].done} of ${counts[g].total}`).join(' · ')}
              </div>
            </div>
            <div className="gmb-pct">{pct}%</div>
          </div>
          <div className="progress-bar" role="progressbar" aria-valuenow={counts.done} aria-valuemin={0} aria-valuemax={counts.total}><span style={{ width: `${pct}%` }} /></div>
        </div>

        <div className="gmb-filters" role="tablist" aria-label="Show tasks for">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={filter === k}
                    className={`gmb-filter ${filter === k ? 'active' : ''}`} onClick={() => setFilter(k)}>
              {label}{k !== 'all' && counts[k] ? <span className="gmb-filter-count">{counts[k].total}</span> : null}
            </button>
          ))}
        </div>
        {preview && <p className="hint gmb-preview-note">Preview: the checkboxes are switched off here. The client ticks the tasks tagged "You".</p>}

        {shown.length === 0 ? (
          <p className="results-empty">{filter === 'client' ? 'Nothing is on your list right now.' : 'No tasks here.'}</p>
        ) : (
          <ul className="gmb-tasks">
            {shown.map((t) => {
              const tickable = !preview && !!onToggle && canTick(t);
              const isOpen = open.has(t.task_id);
              return (
                <li key={t.task_id} className={`gmb-task ${t.done_at ? 'done' : ''} owner-${t.owner_group}`}>
                  <div className="gmb-task-row">
                    <input type="checkbox" className="gmb-check" checked={!!t.done_at} disabled={!tickable}
                           aria-label={`${t.done_at ? 'Mark not done' : 'Mark done'}: ${t.title}`}
                           title={tickable ? undefined : (preview ? 'Preview only' : `${OWNER_LABEL[t.owner_group]} ticks this one`)}
                           onChange={(e) => tickable && onToggle(t, e.target.checked)} />
                    <button type="button" className="gmb-task-title" aria-expanded={isOpen} onClick={() => toggleOpen(t.task_id)}>
                      {t.title}
                    </button>
                    <div className="gmb-task-chips">
                      <span className={`gmb-chip gmb-owner owner-${t.owner_group}`}>{OWNER_LABEL[t.owner_group]}</span>
                      {t.needs_client_approval && <span className="gmb-chip gmb-needs-ok">Needs your OK</span>}
                      {t.priority && <span className={`gmb-chip gmb-prio prio-${String(t.priority).toLowerCase()}`}>{t.priority}</span>}
                      {t.effort && <span className="gmb-chip gmb-effort">{t.effort}</span>}
                      <button type="button" className="link-btn gmb-more" onClick={() => toggleOpen(t.task_id)}>{isOpen ? 'Less' : 'Details'}</button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="gmb-task-detail">
                      {t.found && <><h4>What we found</h4>{paragraphs(t.found).map((p, i) => <p key={i}>{p}</p>)}</>}
                      {t.matters && <><h4>Why it matters</h4>{paragraphs(t.matters).map((p, i) => <p key={i}>{p}</p>)}</>}
                      <h4>What to do</h4>{paragraphs(t.fix).map((p, i) => <p key={i}>{p}</p>)}
                      {t.done_when && <><h4>You'll know it's done when</h4>{paragraphs(t.done_when).map((p, i) => <p key={i}>{p}</p>)}</>}
                      {t.owner_label && <p className="gmb-owner-raw">{t.owner_label}</p>}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
